import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Camera, MapPin, CheckCircle, XCircle, Clock, RefreshCw, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeeAttendance = () => {
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [location, setLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetchTodayAttendance();
    getLocation();
    
    return () => {
      stopCamera();
    };
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await fetch(`${API_BASE_URL}/api/employee/attendance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.attendance && data.attendance.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const todayRecord = data.attendance.find(a => a.date === today);
        setTodayAttendance(todayRecord);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const getLocation = () => {
    setLoadingLocation(true);
    
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      setLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Reverse geocoding using OpenStreetMap Nominatim API
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          
          setLocation({
            latitude,
            longitude,
            city: data.address?.city || data.address?.town || data.address?.village || 'Unknown',
            state: data.address?.state || 'Unknown',
            country: data.address?.country || 'Unknown',
            address: data.display_name || 'Unknown'
          });
          
          toast.success('Location detected successfully');
        } catch (error) {
          console.error('Error fetching location details:', error);
          setLocation({
            latitude,
            longitude,
            city: 'Unknown',
            state: 'Unknown',
            country: 'Unknown',
            address: 'Location detected'
          });
        }
        
        setLoadingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        toast.error('Unable to get your location. Please enable location services.');
        setLoadingLocation(false);
      }
    );
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setCameraActive(true);
        toast.success('Camera started');
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Unable to access camera. Please grant camera permissions.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (!canvasRef.current || !videoRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0);
    
    const photoData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedPhoto(photoData);
    stopCamera();
    
    toast.success('Photo captured successfully');
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    startCamera();
  };

  const markAttendance = async (type) => {
    if (!capturedPhoto) {
      toast.error('Please capture your photo first');
      return;
    }

    if (!location) {
      toast.error('Location not detected. Please enable location services.');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('employeeToken');
      const response = await fetch(`${API_BASE_URL}/api/employee/attendance/mark-with-photo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          photo_data: capturedPhoto,
          latitude: location.latitude,
          longitude: location.longitude,
          city: location.city,
          state: location.state,
          country: location.country,
          address: location.address,
          device_info: navigator.userAgent,
          attendance_type: type
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`${type === 'check_in' ? 'Check-in' : 'Check-out'} successful!`);
        setCapturedPhoto(null);
        fetchTodayAttendance();
      } else {
        toast.error(data.detail || 'Failed to mark attendance');
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      toast.error('Failed to mark attendance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('employeeToken');
    window.location.href = '/employee-portal/login';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header with Logo */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-xl shadow-md p-2">
              <img 
                src="https://netixfibernet.com/static/images/netix-logo.png" 
                alt="Netix Fibernet" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="hidden">
                <Camera className="h-12 w-12 text-blue-600" />
              </div>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Employee Attendance</h1>
              <p className="text-gray-600 mt-1">Mark your attendance with face capture & location</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" className="flex items-center gap-2">
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>

        {/* Today's Status */}
        {todayAttendance && (
          <Card className="mb-6 border-2 border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div>
                  <h3 className="text-lg font-semibold text-green-900">Today's Attendance Marked</h3>
                  <p className="text-green-700">
                    Check-in: {todayAttendance.check_in_time ? new Date(todayAttendance.check_in_time).toLocaleTimeString() : 'Not marked'}
                    {todayAttendance.check_out_time && ` | Check-out: ${new Date(todayAttendance.check_out_time).toLocaleTimeString()}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Location Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-600" />
              Your Location
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingLocation ? (
              <div className="flex items-center gap-2 text-gray-600">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Detecting location...
              </div>
            ) : location ? (
              <div className="space-y-2">
                <p className="text-sm"><strong>City:</strong> {location.city}</p>
                <p className="text-sm"><strong>State:</strong> {location.state}</p>
                <p className="text-sm"><strong>Country:</strong> {location.country}</p>
                <p className="text-xs text-gray-500">{location.address}</p>
              </div>
            ) : (
              <Button onClick={getLocation} variant="outline" size="sm">
                <MapPin className="h-4 w-4 mr-2" />
                Detect Location
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Camera Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-blue-600" />
              Face Capture
            </CardTitle>
            <CardDescription>
              Capture your face photo for attendance verification
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Video/Photo Display */}
              <div className="relative bg-gray-900 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                {!capturedPhoto && (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                    style={{ display: cameraActive ? 'block' : 'none' }}
                  />
                )}
                
                {capturedPhoto && (
                  <img 
                    src={capturedPhoto} 
                    alt="Captured" 
                    className="w-full h-full object-cover"
                  />
                )}
                
                {!cameraActive && !capturedPhoto && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <Camera className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">Camera not started</p>
                    </div>
                  </div>
                )}
                
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>

              {/* Camera Controls */}
              <div className="flex gap-3">
                {!cameraActive && !capturedPhoto && (
                  <Button onClick={startCamera} className="flex-1">
                    <Camera className="h-4 w-4 mr-2" />
                    Start Camera
                  </Button>
                )}
                
                {cameraActive && !capturedPhoto && (
                  <>
                    <Button onClick={capturePhoto} className="flex-1">
                      <Camera className="h-4 w-4 mr-2" />
                      Capture Photo
                    </Button>
                    <Button onClick={stopCamera} variant="outline">
                      Cancel
                    </Button>
                  </>
                )}
                
                {capturedPhoto && (
                  <>
                    <Button onClick={retakePhoto} variant="outline" className="flex-1">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Retake Photo
                    </Button>
                  </>
                )}
              </div>

              {/* Attendance Buttons */}
              {capturedPhoto && location && (
                <div className="grid grid-cols-2 gap-3 pt-4 border-t">
                  <Button
                    onClick={() => markAttendance('check_in')}
                    disabled={submitting || (todayAttendance && todayAttendance.check_in_time)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {submitting ? 'Submitting...' : 'Check In'}
                  </Button>
                  
                  <Button
                    onClick={() => markAttendance('check_out')}
                    disabled={submitting || !todayAttendance || !todayAttendance.check_in_time || todayAttendance.check_out_time}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    {submitting ? 'Submitting...' : 'Check Out'}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmployeeAttendance;
